<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("kol.menu.assignments") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right">
          <base-button size="md" type="neutral" @click="handleCreate()">
            <i class="fa fa-plus"></i> {{ $t("add") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div>
        <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
          <template slot="header">
            <div class="row">
              <div class="col-md-2">
                <base-input>
                  <el-select
                    v-model="filterStaffId"
                    :placeholder="$t('kol.staff_name')"
                    clearable
                    filterable
                    @change="handleSearch"
                  >
                    <el-option
                      v-for="s in staff"
                      :key="s.id"
                      :label="staffLabel(s)"
                      :value="s.id"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>

              <div class="col-md-2">
                <base-input>
                  <el-select
                    v-model="filterCreatorId"
                    :placeholder="$t('kol.creator_name')"
                    clearable
                    filterable
                    @change="handleSearch"
                  >
                    <el-option
                      v-for="c in creators"
                      :key="c.id"
                      :label="c.name"
                      :value="c.id"
                    >
                    </el-option>
                  </el-select>
                </base-input>
              </div>

              <div class="col-md-2">
                <base-input>
                  <el-select
                    v-model="filterStatus"
                    :placeholder="$t('status')"
                    clearable
                    @change="handleSearch"
                  >
                    <el-option :label="$t('kol.status.pending')" value="pending"></el-option>
                    <el-option :label="$t('kol.status.briefed')" value="briefed"></el-option>
                    <el-option :label="$t('kol.status.product_sent')" value="product_sent"></el-option>
                    <el-option :label="$t('kol.status.posted')" value="posted"></el-option>
                    <el-option :label="$t('kol.status.completed')" value="completed"></el-option>
                  </el-select>
                </base-input>
              </div>

              <div class="col-md-2">
                <base-input>
                  <el-select
                    v-model="filterPaymentStatus"
                    :placeholder="$t('kol.payment_status')"
                    clearable
                    @change="handleSearch"
                  >
                    <el-option :label="$t('kol.payment.unpaid')" value="unpaid"></el-option>
                    <el-option :label="$t('kol.payment.paid')" value="paid"></el-option>
                    <el-option :label="$t('kol.payment.no_payment')" value="no_payment"></el-option>
                  </el-select>
                </base-input>
              </div>

              <div class="col-md-3">
                <base-input>
                  <el-date-picker
                    v-model="filterBriefDateRange"
                    type="daterange"
                    range-separator="-"
                    :start-placeholder="$t('kol.brief_date_from')"
                    :end-placeholder="$t('kol.brief_date_to')"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    @change="handleSearch"
                  >
                  </el-date-picker>
                </base-input>
              </div>

              <div class="col-md-2">
                <base-input
                  v-model="searchQuery"
                  prepend-icon="fas fa-search"
                  :placeholder="$t('search')"
                  @keyup.enter.native="handleSearch"
                >
                </base-input>
              </div>

              <div class="col-md-1">
                <base-button type="primary" @click="handleSearch">
                  <i class="fa fa-search"></i>
                </base-button>
              </div>
            </div>
          </template>

          <div>
            <el-table
              :data="assignments"
              row-key="id"
              header-row-class-name="thead-light"
              v-loading="loading"
            >
              <el-table-column :label="$t('kol.code')" min-width="130" prop="code" sortable>
                <div slot-scope="{ row }">
                  {{ row.code || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.staff_name')" min-width="150">
                <div slot-scope="{ row }">
                  {{ row.staff_name || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.creator_name')" min-width="150">
                <div slot-scope="{ row }">
                  {{ row.creator_name || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.brief_date')" min-width="120">
                <div slot-scope="{ row }">
                  {{ formatDate(row.brief_date) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.clip_type')" min-width="120">
                <div slot-scope="{ row }">
                  {{ row.clip_type || '-' }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('status')" min-width="130" align="center">
                <div slot-scope="{ row }">
                  <badge :type="statusBadgeType(row.status)" class="badge-lg">
                    {{ $t('kol.status.' + (row.status || 'pending')) }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.payment_status')" min-width="120" align="center">
                <div slot-scope="{ row }">
                  <badge :type="paymentBadgeType(row.payment_status)" class="badge-lg">
                    {{ $t('kol.payment.' + (row.payment_status || 'unpaid')) }}
                  </badge>
                </div>
              </el-table-column>

              <el-table-column :label="$t('kol.payment_amount')" min-width="130" align="right">
                <div slot-scope="{ row }">
                  {{ formatNumber(row.payment_amount) }}
                </div>
              </el-table-column>

              <el-table-column :label="$t('actions')" min-width="220" align="center">
                <div slot-scope="{ row }" class="d-flex justify-content-center align-items-center">
                  <el-select
                    size="mini"
                    :placeholder="$t('kol.change_status')"
                    style="width: 135px"
                    @change="(val) => handleStatusUpdate(row, val)"
                  >
                    <el-option :label="$t('kol.status.pending')" value="pending"></el-option>
                    <el-option :label="$t('kol.status.briefed')" value="briefed"></el-option>
                    <el-option :label="$t('kol.status.product_sent')" value="product_sent"></el-option>
                    <el-option :label="$t('kol.status.posted')" value="posted"></el-option>
                    <el-option :label="$t('kol.status.completed')" value="completed"></el-option>
                  </el-select>

                  <base-button
                    @click.native="handleViewTimeline(row)"
                    class="ml-1"
                    type="info"
                    size="sm"
                    icon
                    :title="$t('kol.timeline')"
                  >
                    <i class="text-white fa fa-history"></i>
                  </base-button>

                  <base-button
                    @click.native="handleEdit(row)"
                    class="edit ml-1"
                    type="warning"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-edit"></i>
                  </base-button>

                  <base-button
                    @click.native="handleDelete(row)"
                    class="remove btn-link"
                    type="danger"
                    size="sm"
                    icon
                  >
                    <i class="text-white fa fa-trash"></i>
                  </base-button>
                </div>
              </el-table-column>
            </el-table>
          </div>

          <div
            slot="footer"
            class="col-12 d-flex justify-content-center justify-content-sm-between flex-wrap"
          >
            <div class="">
              <p class="card-category">
                {{ $t("data") }} {{ fromRecord }} {{ $t("to") }} {{ toRecord }}
                {{ $t("of") }} {{ pagination.total }}
              </p>
            </div>
            <base-pagination
              class="pagination-no-border"
              v-model="currentPage"
              :per-page="perPage"
              :total="pagination.total"
              @input="handlePageChange"
            >
            </base-pagination>
          </div>
        </card>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <modal :show.sync="showModal" modal-classes="modal-secondary modal-lg modal-dialog-scrollable">
      <h5 slot="header" class="modal-title">
        {{ isEdit ? $t('kol.edit_assignment') : $t('kol.create_assignment') }}
      </h5>

      <form class="new-event--form" @submit.prevent="">
        <div class="form-group">
          <label class="form-control-label">{{ $t('kol.note') }}</label>
          <textarea
            class="form-control"
            v-model="formData.note"
            rows="2"
          ></textarea>
        </div>

        <el-tabs v-model="activeTab" type="border-card">
          <!-- Tab 1: Brief Info -->
          <el-tab-pane :label="$t('kol.tab.brief_info')" name="brief">
            <div class="row">
              <div class="col-md-12">
                <div class="form-group">
                  <label class="form-control-label">{{ $t('kol.creator_name') }} *</label>
                  <el-select v-model="formData.creator_id" class="w-100" filterable clearable>
                    <el-option
                      v-for="c in creators"
                      :key="c.id"
                      :label="c.name"
                      :value="c.id"
                    >
                    </el-option>
                  </el-select>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <base-input :label="$t('kol.code')" v-model="formData.code">
                </base-input>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t('kol.brief_date') }}</label>
                  <el-date-picker
                    v-model="formData.brief_date"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    class="w-100"
                  >
                  </el-date-picker>
                </div>
              </div>
            </div>

            <div class="row">
              <div class="col-md-6">
                <base-input :label="$t('kol.clip_type')" v-model="formData.clip_type">
                </base-input>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t('kol.clip_count') }}</label>
                  <el-input-number
                    v-model="formData.clip_count"
                    :min="0"
                    controls-position="right"
                    class="w-100"
                  >
                  </el-input-number>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.script_detail') }}</label>
              <textarea
                class="form-control"
                v-model="formData.script_detail"
                rows="3"
              ></textarea>
            </div>

            <base-input :label="$t('kol.hashtags')" v-model="formData.hashtags">
            </base-input>
          </el-tab-pane>

          <!-- Tab 2: Shipping -->
          <el-tab-pane :label="$t('kol.tab.shipping')" name="shipping">
            <div class="row align-items-end">
              <div class="col-md-9">
                <base-input :label="$t('kol.order_number')" v-model="formData.order_number">
                </base-input>
              </div>
              <div class="col-md-3 mb-3">
                <base-button size="sm" type="primary" class="w-100" @click="loadOrderProducts()" :disabled="!formData.order_number">
                  <i class="fa fa-search"></i> {{ $t('kol.load_products') }}
                </base-button>
              </div>
            </div>

            <div v-if="orderProductsLoading" class="text-center text-muted py-2">
              <i class="fa fa-spinner fa-spin"></i>
            </div>

            <div v-if="!orderProductsLoading && orderTrackingNumber" class="alert alert-info py-2 mb-3">
              <strong>{{ $t('kol.tracking_number') }}:</strong> {{ orderTrackingNumber }}
            </div>

            <div v-if="orderProducts.length > 0" class="mb-2 font-weight-bold">{{ $t('product') }}</div>
            <el-table
              v-if="orderProducts.length > 0"
              :data="orderProducts"
              size="mini"
              header-row-class-name="thead-light"
              class="mb-3"
            >
              <el-table-column :label="$t('product')" prop="product_name" min-width="220"></el-table-column>
              <el-table-column :label="$t('sku')" prop="sku" min-width="120"></el-table-column>
              <el-table-column :label="$t('quantity')" prop="quantity" min-width="80" align="right"></el-table-column>
              <el-table-column :label="$t('amount')" prop="amount" min-width="110" align="right">
                <template slot-scope="scope">{{ formatNumber(scope.row.amount) }}</template>
              </el-table-column>
            </el-table>

            <div v-if="orderWmsProducts.length > 0" class="mb-2 font-weight-bold">{{ $t('wms_product') }}</div>
            <el-table
              v-if="orderWmsProducts.length > 0"
              :data="orderWmsProducts"
              size="mini"
              header-row-class-name="thead-light"
              class="mb-3"
            >
              <el-table-column :label="$t('wms_product')" prop="wms_product_name" min-width="260"></el-table-column>
              <el-table-column :label="$t('quantity')" prop="quantity" min-width="80" align="right"></el-table-column>
            </el-table>

            <div v-if="orderProductsLoaded && orderProducts.length === 0 && orderWmsProducts.length === 0" class="text-muted small mb-3">
              {{ $t('no_data') }}
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.product_received_date') }}</label>
              <el-date-picker
                v-model="formData.product_received_date"
                type="date"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                class="w-100"
              >
              </el-date-picker>
            </div>
          </el-tab-pane>

          <!-- Tab 3: Content -->
          <el-tab-pane :label="$t('kol.tab.content')" name="content">
            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.post_date') }}</label>
              <el-date-picker
                v-model="formData.post_date"
                type="date"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                class="w-100"
              >
              </el-date-picker>
            </div>

            <base-input :label="$t('kol.original_file_link')" v-model="formData.original_file_link">
            </base-input>

            <base-input :label="$t('kol.link_fb')" v-model="formData.link_fb">
            </base-input>

            <base-input :label="$t('kol.link_lemon8')" v-model="formData.link_lemon8">
            </base-input>

            <base-input :label="$t('kol.link_ig')" v-model="formData.link_ig">
            </base-input>

            <base-input :label="$t('kol.link_tiktok')" v-model="formData.link_tiktok">
            </base-input>

            <base-input :label="$t('kol.gencode')" v-model="formData.gencode">
            </base-input>

            <div class="row">
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t('kol.duration_days') }}</label>
                  <el-input-number
                    v-model="formData.duration_days"
                    :min="0"
                    controls-position="right"
                    class="w-100"
                  >
                  </el-input-number>
                </div>
              </div>
              <div class="col-md-6">
                <div class="form-group">
                  <label class="form-control-label">{{ $t('kol.expiry_date') }}</label>
                  <el-date-picker
                    v-model="formData.expiry_date"
                    type="date"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    class="w-100"
                  >
                  </el-date-picker>
                </div>
              </div>
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.ads_tiktok') }}</label>
              <div>
                <el-switch
                  v-model="formData.ads_tiktok"
                  active-color="#13ce66"
                  inactive-color="#909399"
                >
                </el-switch>
                <span class="ml-2">{{ formData.ads_tiktok ? $t('yes') : $t('no') }}</span>
              </div>
            </div>
          </el-tab-pane>

          <!-- Tab 4: Payment -->
          <el-tab-pane :label="$t('kol.tab.payment')" name="payment">
            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.payment_amount') }}</label>
              <el-input-number
                v-model="formData.payment_amount"
                :min="0"
                :precision="2"
                controls-position="right"
                class="w-100"
              >
              </el-input-number>
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.tax_type') }}</label>
              <el-select v-model="formData.tax_type" class="w-100">
                <el-option :label="$t('kol.tax.none')" value="none"></el-option>
                <el-option label="3%" value="3%"></el-option>
                <el-option label="5%" value="5%"></el-option>
              </el-select>
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.payment_status') }}</label>
              <el-select v-model="formData.payment_status" class="w-100" :disabled="isEdit">
                <el-option :label="$t('kol.payment.unpaid')" value="unpaid"></el-option>
                <el-option :label="$t('kol.payment.paid')" value="paid"></el-option>
                <el-option :label="$t('kol.payment.no_payment')" value="no_payment"></el-option>
              </el-select>
            </div>

            <div class="form-group">
              <label class="form-control-label">{{ $t('kol.payment_date') }}</label>
              <el-date-picker
                v-model="formData.payment_date"
                type="date"
                format="yyyy-MM-dd"
                value-format="yyyy-MM-dd"
                class="w-100"
              >
              </el-date-picker>
            </div>
          </el-tab-pane>
        </el-tabs>
      </form>

      <template slot="footer">
        <button type="submit" class="btn btn-md btn-primary" @click="handleSave()">
          {{ $t("save") }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>

    <!-- Timeline Modal -->
    <modal :show.sync="showTimelineModal" modal-classes="modal-secondary modal-lg">
      <h5 slot="header" class="modal-title">
        {{ $t('kol.timeline') }}
        <span v-if="timelineAssignment" class="text-muted">— {{ timelineAssignment.code || ('#' + timelineAssignment.id) }}</span>
      </h5>

      <div v-loading="timelineLoading">
        <div v-if="!timelineLoading && timelineItems.length === 0" class="text-center text-muted p-3">
          {{ $t('no_data') }}
        </div>
        <ul v-else class="list-unstyled timeline-list">
          <li v-for="item in timelineItems" :key="item.id" class="timeline-item">
            <span class="timeline-dot" :class="'bg-' + timelineDotType(item.action)"></span>
            <div class="timeline-content">
              <div class="text-muted small">{{ formatDateTime(item.created_at) }}</div>
              <div>
                <strong>{{ timelineActionLabel(item) }}</strong>
                <span v-if="item.old_value || item.new_value" class="ml-2">
                  <span v-if="item.old_value" class="text-muted">{{ timelineValueLabel(item.action, item.old_value) }}</span>
                  <span v-if="item.old_value && item.new_value"> → </span>
                  <span v-if="item.new_value" class="text-primary">{{ timelineValueLabel(item.action, item.new_value) }}</span>
                </span>
              </div>
              <div class="text-muted small" v-if="item.user_name">{{ $t('kol.staff') }}: {{ item.user_name }}</div>
              <div class="small" v-if="item.note">{{ item.note }}</div>
            </div>
          </li>
        </ul>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showTimelineModal = false">
          {{ $t("close") }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Tabs, TabPane, DatePicker, InputNumber, Switch } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination, Badge } from "@/components";
import clientPaginationMixin from "../PaginatedTables/clientPaginationMixin";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";

export default {
  mixins: [clientPaginationMixin],
  components: {
    BasePagination,
    RouteBreadCrumb,
    Badge,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [DatePicker.name]: DatePicker,
    [InputNumber.name]: InputNumber,
    [Switch.name]: Switch,
  },
  computed: {
    ...mapGetters({
      tableData: "KOL/getAssignments",
      staff: "KOL/getStaff",
      creators: "KOL/getCreators",
    }),
    pagination() {
      return this.$store.getters["KOL/getAssignmentsPagination"];
    },
    fromRecord() {
      return (this.currentPage - 1) * this.perPage + 1;
    },
    toRecord() {
      const to = this.currentPage * this.perPage;
      return to > this.pagination.total ? this.pagination.total : to;
    },
    assignments() {
      return this.tableData;
    },
  },
  data() {
    return {
      propsToSearch: ["code", "staff_name", "creator_name"],
      loading: false,
      showModal: false,
      isEdit: false,
      activeTab: "brief",
      searchQuery: "",
      filterStaffId: null,
      filterCreatorId: null,
      showTimelineModal: false,
      timelineLoading: false,
      timelineAssignment: null,
      timelineItems: [],
      orderProducts: [],
      orderWmsProducts: [],
      orderTrackingNumber: "",
      orderProductsLoading: false,
      orderProductsLoaded: false,
      filterStatus: "",
      filterPaymentStatus: "",
      filterBriefDateRange: [],
      currentPage: 1,
      perPage: 50,
      formData: {
        id: 0,
        staff_id: null,
        creator_id: null,
        code: "",
        brief_date: null,
        clip_type: "",
        clip_count: 1,
        script_detail: "",
        hashtags: "",
        cart_product: "",
        quantity: 1,
        color_variant: "",
        address: "",
        order_number: "",
        tracking_number: "",
        product_received_date: null,
        post_date: null,
        original_file_link: "",
        link_fb: "",
        link_ig: "",
        link_tiktok: "",
        link_lemon8: "",
        gencode: "",
        duration_days: 0,
        expiry_date: null,
        ads_tiktok: false,
        payment_amount: 0,
        tax_type: "none",
        payment_status: "unpaid",
        payment_date: null,
        note: "",
      },
    };
  },
  mounted() {
    const q = this.$route.query || {};
    if (q.staff_id) this.filterStaffId = parseInt(q.staff_id, 10) || null;
    if (q.creator_id) this.filterCreatorId = parseInt(q.creator_id, 10) || null;
    if (q.status) this.filterStatus = q.status;
    if (q.payment_status) this.filterPaymentStatus = q.payment_status;
    if (q.brief_date_from && q.brief_date_to) {
      this.filterBriefDateRange = [q.brief_date_from, q.brief_date_to];
    }
    this.loadData();
  },
  methods: {
    ...mapActions({
      listStaff: "KOL/listStaff",
      listCreators: "KOL/listCreators",
      listAssignments: "KOL/listAssignments",
      createAssignment: "KOL/createAssignment",
      updateAssignment: "KOL/updateAssignment",
      deleteAssignment: "KOL/deleteAssignment",
      updateAssignmentStatus: "KOL/updateAssignmentStatus",
      updateAssignmentPayment: "KOL/updateAssignmentPayment",
      getAssignmentTimeline: "KOL/getAssignmentTimeline",
      getAssignmentOrderItems: "KOL/getAssignmentOrderItems",
    }),
    resetFormData() {
      return {
        id: 0,
        staff_id: null,
        creator_id: null,
        code: "",
        brief_date: null,
        clip_type: "",
        clip_count: 1,
        script_detail: "",
        hashtags: "",
        cart_product: "",
        quantity: 1,
        color_variant: "",
        address: "",
        order_number: "",
        tracking_number: "",
        product_received_date: null,
        post_date: null,
        original_file_link: "",
        link_fb: "",
        link_ig: "",
        link_tiktok: "",
        link_lemon8: "",
        gencode: "",
        duration_days: 0,
        expiry_date: null,
        ads_tiktok: false,
        payment_amount: 0,
        tax_type: "none",
        payment_status: "unpaid",
        payment_date: null,
        note: "",
      };
    },
    async loadData() {
      this.loading = true;
      try {
        await Promise.all([
          this.listStaff({}),
          this.listCreators({ per_page: 500 }),
          this.fetchAssignments(),
        ]);
      } catch (error) {
        console.log(error);
      } finally {
        this.loading = false;
      }
    },
    async fetchAssignments() {
      const params = {
        page: this.currentPage,
        per_page: this.perPage,
      };
      if (this.searchQuery) params.search = this.searchQuery;
      if (this.filterStaffId) params.staff_id = this.filterStaffId;
      if (this.filterCreatorId) params.creator_id = this.filterCreatorId;
      if (this.filterStatus) params.status = this.filterStatus;
      if (this.filterPaymentStatus) params.payment_status = this.filterPaymentStatus;
      if (this.filterBriefDateRange && this.filterBriefDateRange.length === 2) {
        params.brief_date_from = this.filterBriefDateRange[0];
        params.brief_date_to = this.filterBriefDateRange[1];
      }
      await this.listAssignments(params);
    },
    handleSearch() {
      this.currentPage = 1;
      this.loading = true;
      this.fetchAssignments()
        .catch((error) => console.log(error))
        .finally(() => { this.loading = false; });
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.loading = true;
      this.fetchAssignments()
        .catch((error) => console.log(error))
        .finally(() => { this.loading = false; });
    },
    handleCreate() {
      this.isEdit = false;
      this.formData = this.resetFormData();
      this.activeTab = "brief";
      this.orderProducts = [];
      this.orderWmsProducts = [];
      this.orderTrackingNumber = "";
      this.orderProductsLoaded = false;
      this.showModal = true;
    },
    handleEdit(row) {
      this.isEdit = true;
      this.formData = {
        id: row.id,
        staff_id: row.staff_id || null,
        creator_id: row.creator_id || null,
        code: row.code || "",
        brief_date: row.brief_date ? row.brief_date.substring(0, 10) : null,
        clip_type: row.clip_type || "",
        clip_count: row.clip_count || 1,
        script_detail: row.script_detail || "",
        hashtags: row.hashtags || "",
        cart_product: row.cart_product || "",
        quantity: row.quantity || 1,
        color_variant: row.color_variant || "",
        address: row.address || "",
        order_number: row.order_number || "",
        tracking_number: row.tracking_number || "",
        product_received_date: row.product_received_date ? row.product_received_date.substring(0, 10) : null,
        post_date: row.post_date ? row.post_date.substring(0, 10) : null,
        original_file_link: row.original_file_link || "",
        link_fb: row.link_fb || "",
        link_ig: row.link_ig || "",
        link_tiktok: row.link_tiktok || "",
        link_lemon8: row.link_lemon8 || "",
        gencode: row.gencode || "",
        duration_days: row.duration_days || 0,
        expiry_date: row.expiry_date ? row.expiry_date.substring(0, 10) : null,
        ads_tiktok: row.ads_tiktok || false,
        payment_amount: row.payment_amount || 0,
        tax_type: row.tax_type || "none",
        payment_status: row.payment_status || "unpaid",
        payment_date: row.payment_date ? row.payment_date.substring(0, 10) : null,
        note: row.note || "",
      };
      this.activeTab = "brief";
      this.orderProducts = [];
      this.orderWmsProducts = [];
      this.orderTrackingNumber = "";
      this.orderProductsLoaded = false;
      this.showModal = true;
      if (row.order_number) {
        this.$nextTick(() => this.loadOrderProducts());
      }
    },
    handleSave() {
      var self = this;
      if (!this.formData.creator_id) {
        swal.fire({
          title: this.$t("errors.invalid_param"),
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
        return;
      }

      const payload = {
        staff_id: this.formData.staff_id,
        creator_id: this.formData.creator_id,
        code: this.formData.code,
        brief_date: this.formData.brief_date,
        clip_type: this.formData.clip_type,
        clip_count: this.formData.clip_count,
        script_detail: this.formData.script_detail,
        hashtags: this.formData.hashtags,
        cart_product: this.formData.cart_product,
        quantity: this.formData.quantity,
        color_variant: this.formData.color_variant,
        address: this.formData.address,
        order_number: this.formData.order_number,
        tracking_number: this.formData.tracking_number,
        product_received_date: this.formData.product_received_date,
        post_date: this.formData.post_date,
        original_file_link: this.formData.original_file_link,
        link_fb: this.formData.link_fb,
        link_lemon8: this.formData.link_lemon8,
        link_ig: this.formData.link_ig,
        link_tiktok: this.formData.link_tiktok,
        gencode: this.formData.gencode,
        duration_days: this.formData.duration_days,
        expiry_date: this.formData.expiry_date,
        ads_tiktok: this.formData.ads_tiktok,
        payment_amount: this.formData.payment_amount,
        tax_type: this.formData.tax_type,
        note: this.formData.note,
      };
      // Only include status fields on create, not edit
      if (!this.isEdit) {
        payload.status = this.formData.status || 'pending';
        payload.payment_status = this.formData.payment_status || 'unpaid';
      }

      if (this.isEdit) {
        this.updateAssignment({ id: this.formData.id, payload })
          .then(function () {
            self.showModal = false;
            self.fetchAssignments();
            swal.fire({
              title: self.$t("edit_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      } else {
        this.createAssignment(payload)
          .then(function () {
            self.showModal = false;
            self.fetchAssignments();
            swal.fire({
              title: self.$t("create_success"),
              icon: "success",
              confirmButtonClass: "btn btn-success btn-fill",
              buttonsStyling: false,
            });
          })
          .catch(function (err) {
            var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
            swal.fire({
              title: self.$t("error"),
              text: errorMsg,
              icon: "error",
              confirmButtonClass: "btn btn-danger btn-fill",
              buttonsStyling: false,
            });
          });
      }
    },
    handleDelete(row) {
      var self = this;
      swal
        .fire({
          title: this.$t("delete_confirm"),
          text: this.$t("delete_confirm_question") + (row.code || row.id),
          icon: "warning",
          showCancelButton: true,
          confirmButtonClass: "btn btn-success btn-fill",
          cancelButtonClass: "btn btn-danger btn-fill",
          confirmButtonText: this.$t("confirm"),
          cancelButtonText: this.$t("cancel"),
          buttonsStyling: false,
        })
        .then(function (result) {
          if (result.value) {
            self.deleteAssignment(row.id)
              .then(function () {
                self.fetchAssignments();
                swal.fire({
                  title: self.$t("delete_success"),
                  icon: "success",
                  confirmButtonClass: "btn btn-success btn-fill",
                  buttonsStyling: false,
                });
              })
              .catch(function (err) {
                var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
                swal.fire({
                  title: self.$t("error"),
                  text: errorMsg,
                  icon: "error",
                  confirmButtonClass: "btn btn-danger btn-fill",
                  buttonsStyling: false,
                });
              });
          }
        });
    },
    handleStatusUpdate(row, newStatus) {
      if (!newStatus) return;
      var self = this;
      this.updateAssignmentStatus({ id: row.id, payload: { status: newStatus } })
        .then(function () {
          self.fetchAssignments();
          swal.fire({
            title: self.$t("edit_success"),
            icon: "success",
            confirmButtonClass: "btn btn-success btn-fill",
            buttonsStyling: false,
            timer: 1500,
          });
        })
        .catch(function (err) {
          var errorMsg = (err.response && err.response.data && err.response.data.message) || "Error";
          swal.fire({
            title: self.$t("error"),
            text: errorMsg,
            icon: "error",
            confirmButtonClass: "btn btn-danger btn-fill",
            buttonsStyling: false,
          });
        });
    },
    staffLabel(s) {
      if (!s) return "";
      const full = [s.fname, s.lname].filter(Boolean).join(" ").trim();
      if (s.nick_name && full) return `${full} (${s.nick_name})`;
      return s.nick_name || full || s.email || `#${s.id}`;
    },
    async loadOrderProducts() {
      const num = (this.formData.order_number || "").trim();
      if (!num) return;
      this.orderProductsLoading = true;
      this.orderProductsLoaded = false;
      try {
        const res = await this.getAssignmentOrderItems(num);
        const datum = (res.data && res.data.Datum) || {};
        this.orderProducts = datum.items || [];
        this.orderWmsProducts = datum.wms_items || [];
        this.orderTrackingNumber = datum.tracking_number || "";
        this.formData.tracking_number = this.orderTrackingNumber;
      } catch (err) {
        this.orderProducts = [];
        this.orderWmsProducts = [];
        this.orderTrackingNumber = "";
        swal.fire({
          text: (err.response && err.response.data && err.response.data.Msg) || err.message,
          title: "Error",
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
      } finally {
        this.orderProductsLoaded = true;
        this.orderProductsLoading = false;
      }
    },
    async handleViewTimeline(row) {
      this.timelineAssignment = row;
      this.timelineItems = [];
      this.showTimelineModal = true;
      this.timelineLoading = true;
      try {
        const res = await this.getAssignmentTimeline(row.id);
        this.timelineItems = (res.data && res.data.Data) || [];
      } catch (err) {
        swal.fire({
          text: (err.response && err.response.data && err.response.data.Msg) || err.message,
          title: "Error",
          icon: "error",
          confirmButtonClass: "btn btn-danger btn-fill",
          buttonsStyling: false,
        });
      } finally {
        this.timelineLoading = false;
      }
    },
    timelineDotType(action) {
      switch (action) {
        case "created":
          return "primary";
        case "status_change":
          return "warning";
        case "payment_change":
          return "success";
        default:
          return "info";
      }
    },
    timelineActionLabel(item) {
      const key = "kol.timeline_action." + (item.action || "updated");
      const translated = this.$t(key);
      if (translated !== key) return translated;
      return item.action;
    },
    timelineValueLabel(action, val) {
      if (!val) return "";
      if (action === "status_change" || action === "created") {
        const key = "kol.status." + val;
        const t = this.$t(key);
        return t !== key ? t : val;
      }
      if (action === "payment_change") {
        const key = "kol.payment." + val;
        const t = this.$t(key);
        return t !== key ? t : val;
      }
      return val;
    },
    formatDateTime(ts) {
      if (!ts) return "-";
      const d = new Date(ts);
      if (isNaN(d.getTime())) return ts;
      const pad = (n) => (n < 10 ? "0" + n : "" + n);
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    statusBadgeType(status) {
      const map = {
        pending: "info",
        briefed: "primary",
        product_sent: "warning",
        posted: "success",
        completed: "default",
      };
      return map[status] || "info";
    },
    paymentBadgeType(status) {
      if (status === "paid") return "success";
      if (status === "no_payment") return "default";
      return "warning";
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      return dateString.substring(0, 10);
    },
    formatNumber(num) {
      if (!num && num !== 0) return "-";
      return parseFloat(num).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style>
.no-border-card .card-footer {
  border-top: 0;
}
.badge-lg {
  font-size: 0.8rem;
  padding: 0.5em 0.8em;
}
.timeline-list {
  margin: 0;
  padding-left: 20px;
  border-left: 2px solid #e9ecef;
}
.timeline-item {
  position: relative;
  padding: 0 0 18px 16px;
}
.timeline-item:last-child {
  padding-bottom: 0;
}
.timeline-dot {
  position: absolute;
  left: -26px;
  top: 6px;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  box-shadow: 0 0 0 3px #fff;
}
.timeline-dot.bg-primary { background: #5e72e4; }
.timeline-dot.bg-success { background: #2dce89; }
.timeline-dot.bg-warning { background: #fb6340; }
.timeline-dot.bg-info { background: #11cdef; }
.timeline-content { line-height: 1.4; }
</style>
